import {
  fetchAssetReferralById,
  fetchProsecutors,
  updateAssetReferral,
  updateAssetReferralProhibitionRequest,
} from "services/assetRecoveryService";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BasePopup } from "./BasePopup";
import {
  AssetReferralActionStatusEnum,
  AssetReferralForwardingStatusEnum,
  LoginAccount,
  ProhibitionFormData,
  ProhibitionRequest,
  Referral,
} from "types";
import { useTooltip } from "context/TooltipProvider";
import { RespondBackPopup } from "./RespondBackPopup";
import { ProhibitionSection } from "components/referralDetails/ProhibitionSection";
import CourtSection from "components/referralDetails/CourtSection";
import { errorHandlerOnForm } from "utils/helper";
import { ProhibitionCourtPopup } from "./ProhibitionCourtPopup";
import FileRow from "components/file-components/file-row";
import { ProsecutorAssignmentSection } from "components/referralDetails/ProsecutorAssignment";
import { useAuth } from "context/AuthContext";

interface ProhibitionDetailPopupProps {
  isOpen: boolean;
  onClose: () => void;
  prohibitionData: ProhibitionRequest | null;
  loading?: boolean;
  isAssetProsecutor: boolean;
  isAssetCoordinator: boolean;
  isAssetRegistrar: boolean;
  onDecsionCreated?: (data: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  referralId: string;
  referral: Referral;
  setReferralData: React.Dispatch<React.SetStateAction<Referral | null>>;
  onNewDataAdded: (data: Referral) => void;
  fileRowProps: any;
}

const defaultProhibitionFormData: ProhibitionFormData = {
  prohabition_request_registration_number: null,
  prohabiton_request_date_registrar: null,
};

export const ProhibitionDetailPopup: React.FC<ProhibitionDetailPopupProps> = ({
  isOpen,
  onClose,
  prohibitionData,
  loading = false,
  isAssetProsecutor,
  isAssetCoordinator,
  isAssetRegistrar,
  onDecsionCreated,
  setActiveTab,
  referralId,
  referral,
  setReferralData,
  onNewDataAdded,
  fileRowProps,
}) => {
  const [isRespondBackOpen, setIsRespondBackOpen] = useState(false);
  const [isForwarding, setIsForwarding] = useState(false);
  const [prohibitionDataState, setProhibitionDataState] =
    useState<ProhibitionRequest | null>(prohibitionData);
  const [status, setStatus] = useState(prohibitionDataState?.status || "");
  const { showTooltip } = useTooltip();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isProhibitionCourtPopupOpen, setIsProhibitionCourtPopupOpen] =
    useState(false);
  const [prohibitionFormData, setProhibitionFormData] =
    useState<ProhibitionFormData>(defaultProhibitionFormData);
  const [isEditingProhibition, setIsEditingProhibition] = useState(false);
  const [showProhibitionForm, setShowProhibitionForm] = useState(false);
  const [selectedProsecutor, setSelectedProsecutor] = useState("");
  const [prosecutors, setProsecutors] = useState<any[]>([]);
  const { loginData } = useAuth();
  const { t } = useTranslation();

  // Initialize prohibitionDataState when prohibitionData changes
  useEffect(() => {
    if (prohibitionData) {
      setProhibitionDataState(prohibitionData);
      setStatus(prohibitionData.status || "");

      // Set selected prosecutor if exists
      if (prohibitionData.prosecutor_id) {
        setSelectedProsecutor(prohibitionData.prosecutor_id);
      }
    }
  }, [prohibitionData]);

  // Set edit mode and initialize form data
  useEffect(() => {
    if (referralId && referral && prohibitionDataState) {
      setIsEditMode(true);

      // Set prohibition form data
      setProhibitionFormData({
        prohabition_request_registration_number:
          prohibitionDataState.prohabition_request_registration_number || null,
        prohabiton_request_date_registrar:
          prohibitionDataState.prohabiton_request_date_registrar || null,
      });

      // Show prohibition form if both fields are null and status is correct
      if (
        !prohibitionDataState.prohabition_request_registration_number &&
        !prohibitionDataState.prohabiton_request_date_registrar &&
        prohibitionDataState?.status ===
          AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED
      ) {
        setShowProhibitionForm(true);
      }
    }
  }, [prohibitionDataState, referralId, referral]);

  // Fetch prosecutors when conditions are met
  useEffect(() => {
    const shouldFetchProsecutors =
      isEditMode &&
      isAssetCoordinator &&
      prohibitionDataState?.status ===
        AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED &&
      prohibitionDataState?.forward_status ===
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR &&
      loginData;

    if (shouldFetchProsecutors) {
      fetchProsecutors(loginData as LoginAccount)
        .then((data) => {
          setProsecutors(data);
        })
        .catch((error) => {
          console.error("Error fetching prosecutors:", error);
          showTooltip(t("error_fetching_prosecutors"), "error");
        });
    }
  }, [
    isAssetCoordinator,
    isEditMode,
    prohibitionDataState?.forward_status,
    prohibitionDataState?.status,
    loginData,
    showTooltip,
  ]);

  const handleProhibitionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProhibitionFormData((prevState) => ({
      ...prevState,
      [name]: value || null,
    }));
  };

  // Refresh function to fetch latest data
  const refreshProhibitionData = async () => {
    if (referralId) {
      try {
        const updatedReferral = await fetchAssetReferralById(referralId);
        setReferralData(updatedReferral);
        onNewDataAdded(updatedReferral);

        // Find and update the prohibition data from the updated referral
        if (updatedReferral?.prohibition_record) {
          const updatedProhibition = updatedReferral.prohibition_record.find(
            (p: ProhibitionRequest) => p.id === prohibitionDataState?.id,
          );
          if (updatedProhibition) {
            setProhibitionDataState(updatedProhibition);
          }
        }
      } catch (error) {
        showTooltip(
          t("failed_to_refresh_prohibition_data", {
            defaultValue: "Failed to refresh prohibition data",
          }),
          "error",
        );
      }
    }
  };

  if (!prohibitionDataState) return null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t("not_set");
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatNullableValue = (value: string | null) => {
    return value || t("not_specified");
  };

  const formatArrayValue = (value: string[]) => {
    return value.length > 0
      ? value.join(", ")
      : t("no_assets", { defaultValue: "No assets" });
  };

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
      <span className="text-sm font-medium text-gray-600">{label}:</span>
      <span className="text-sm text-gray-900 col-span-2">{value}</span>
    </div>
  );

  const SectionTitle = ({ title }: { title: string }) => (
    <h3 className="text-md font-semibold text-gray-800 mt-4 mb-2 pb-1 border-b border-gray-200">
      {title}
    </h3>
  );

  const canAssignProsecutor =
    isEditMode &&
    isAssetCoordinator &&
    prohibitionDataState?.forward_status ===
      AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR &&
    prohibitionDataState?.status ===
      AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED;

  const handleProhibitionSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateProhibitionForm();

    if (error) {
      showTooltip(error, "error");
      return;
    }

    if (!referralId) {
      showTooltip(
        t("referral_id_required", { defaultValue: "Referral ID is required" }),
        "error",
      );
      return;
    }

    try {
      const response = await updateAssetReferralProhibitionRequest(
        prohibitionDataState.id,
        prohibitionFormData,
      );
      showTooltip(
        t("prohibition_details_updated_successfully", {
          defaultValue: "Prohibition details updated successfully!",
        }),
        "success",
      );
      setIsEditingProhibition(false);
      setShowProhibitionForm(false);

      // Refresh data after successful update
      if (response && referralId) {
        await refreshProhibitionData();
        setActiveTab?.("Prohibition");
      }
    } catch (error) {
      showTooltip(
        errorHandlerOnForm(error as { message: string }) ||
          t("failed_to_update_prohibition_details", {
            defaultValue: "Failed to update prohibition details",
          }),
        "error",
      );
    }
  };

  const handleProhibitionEditClick = () => {
    setIsEditingProhibition(true);
    setShowProhibitionForm(true);
  };

  const handleCancelProhibitionEdit = () => {
    if (referralId && referral) {
      // Reset prohibition form data from the passed referral prop
      setProhibitionFormData({
        prohabition_request_registration_number:
          referral.prohabition_request_registration_number || null,
        prohabiton_request_date_registrar:
          referral.prohabiton_request_date_registrar || null,
      });
      setIsEditingProhibition(false);

      // Hide form if both fields are not null (data exists)
      if (
        referral.prohabition_request_registration_number ||
        referral.prohabiton_request_date_registrar
      ) {
        setShowProhibitionForm(false);
      }
    }
  };

  const handleAddProhibitionDetails = () => {
    setShowProhibitionForm(true);
    setIsEditingProhibition(true);
  };

  const validateProhibitionForm = () => {
    if (!prohibitionFormData.prohabition_request_registration_number)
      return t("prohibition_request_registration_number_required", {
        defaultValue: "Prohibition Request Registration Number is Required.",
      });
    if (!prohibitionFormData.prohabiton_request_date_registrar)
      return t("prohibition_request_date_required", {
        defaultValue: "Prohibition Request Date is Required.",
      });
    return "";
  };

  const handleAddReportDetails = () => {
    if (prohibitionDataState?.id) {
      setActiveTab?.("Prohibition Reports");
    }
  };
  const handleProhibitionForwardToCoordinator = async () => {
    try {
      const formData = new FormData();
      formData.append(
        "forward_status",
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR,
      );

      const response = await updateAssetReferralProhibitionRequest(
        prohibitionData?.id as string,
        formData,
      );
      showTooltip(t("successfully_forwarded"), "success");

      if (response && referralId) {
        await refreshProhibitionData();
        setActiveTab?.("Prohibition");
      }
    } catch (error) {
      console.error("Error forwarding to coordinator:", error);
      showTooltip(
        t("failed_to_forward_to_coordinator", {
          defaultValue: "Failed to forward to coordinator.",
        }),
        "error",
      );
    }
  };

  const handleAssignReAssign = async () => {
    if (!selectedProsecutor) {
      showTooltip(t("please_select_prosecutor"), "error");
      return;
    }

    try {
      const formData = new FormData();
      if (
        prohibitionDataState?.status ===
        AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED
      ) {
        formData.append(
          "forward_status",
          AssetReferralForwardingStatusEnum?.ASSIGNED_TO_PROSECUTOR,
        );
        formData.append(
          "status",
          AssetReferralActionStatusEnum?.PROHIBITION_ON_LITIGATION,
        );
      }

      // Only update assigned_prosecutor if a new one is selected
      if (
        selectedProsecutor &&
        selectedProsecutor !== prohibitionDataState?.prosecutor_id
      ) {
        // Find the selected prosecutor from the prosecutors list
        const selectedProsecutorData = prosecutors.find(
          (pros) => pros?.user?.id === selectedProsecutor,
        );

        if (selectedProsecutorData) {
          formData.append(
            "assigned_prosecutor",
            `${selectedProsecutorData.user?.first_name} ${selectedProsecutorData.user?.last_name}`,
          );
          formData.append("prosecutor_id", selectedProsecutor);
        }
      }

      const response = await updateAssetReferralProhibitionRequest(
        prohibitionDataState.id as string,
        formData,
      );
      showTooltip(t("successfully_assigned"), "success");

      // Refresh referral data
      if (response) {
        await refreshProhibitionData();
        setActiveTab?.("Prohibition");
      }
    } catch (error) {
      console.error("Error assigning:", error);
      showTooltip(
        t("failed_to_assign_prosecutor", {
          defaultValue: "Failed to assign prosecutor.",
        }),
        "error",
      );
    }
  };

  const hasCourtData = Boolean(
    prohibitionDataState.prohabition_court_name ||
    prohibitionDataState.prohabition_court_level ||
    prohibitionDataState.prohabition_court_region ||
    prohibitionDataState.prohabition_court_zone ||
    prohibitionDataState.prohabition_court_woreda ||
    prohibitionDataState.prohabition_request_court_registration_number ||
    prohibitionDataState.prohabition_request_first_hearing_date,
  );

  const hasRegistrationData = Boolean(
    prohibitionDataState.prohabition_request_registration_number &&
    prohibitionDataState.prohabiton_request_date_registrar,
  );

  const handleForwardToCoordinator = async () => {
    try {
      setIsForwarding(true);

      const formData = new FormData();
      if (
        prohibitionDataState.status ===
        AssetReferralActionStatusEnum?.PROHIBITION_ON_REVISION
      ) {
        formData.append(
          "status",
          AssetReferralActionStatusEnum?.PROHIBITION_ON_UPDATED,
        );
        setStatus(AssetReferralActionStatusEnum?.PROHIBITION_ON_UPDATED);
      } else if (
        prohibitionDataState.status ===
        AssetReferralActionStatusEnum?.INVESTIGATION_ONGOING
      ) {
        formData.append(
          "status",
          AssetReferralActionStatusEnum?.PROHIBITION_REQUESTED,
        );
        setStatus(AssetReferralActionStatusEnum?.PROHIBITION_REQUESTED);
      }
      formData.append(
        "forward_status",
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR,
      );

      const response = await updateAssetReferralProhibitionRequest(
        prohibitionDataState.id as string,
        formData,
      );
      showTooltip(t("successfully_forwarded"), "success");

      if (response) {
        await refreshProhibitionData();
        setActiveTab?.("Prohibition");
      }
    } catch (error) {
      console.error("Error forwarding to coordinator:", error);
      showTooltip(
        t("failed_to_forward_to_coordinator", {
          defaultValue: "Failed to forward to coordinator.",
        }),
        "error",
      );
    } finally {
      setIsForwarding(false);
    }
  };

  const handleProsecutorApproveDecision = async () => {
    try {
      setIsForwarding(true);

      const formData = new FormData();
      formData.append(
        "status",
        AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED,
      );
      formData.append(
        "forward_status",
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_REGISTRAR,
      );

      const response = await updateAssetReferralProhibitionRequest(
        prohibitionDataState.id as string,
        formData,
      );

      if (response) {
        await refreshProhibitionData();
        setActiveTab?.("Prohibition");
      }

      setStatus(AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED);
      showTooltip(
        t("prohibition_request_approved_successfully", {
          defaultValue: "Prohibition Request Approved successfully",
        }),
        "success",
      );
    } catch (error) {
      console.error("Error approving prohibition:", error);
      showTooltip(
        t("failed_to_approve_prohibition", {
          defaultValue: "Failed to approve prohibition.",
        }),
        "error",
      );
    } finally {
      setIsForwarding(false);
    }
  };

  // Handle court popup success
  const handleCourtPopupSuccess = async () => {
    await refreshProhibitionData();
    setIsProhibitionCourtPopupOpen(false);
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={onClose}
      title={t("prohibition_details", { defaultValue: "Prohibition Details" })}
      description={``}
      position="center"
      w="700px"
    >
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      ) : (
        <div className="max-h-[70vh] overflow-y-auto px-1">
          {isAssetProsecutor && (
            <>
              {(prohibitionDataState.status?.toLowerCase() ===
                AssetReferralActionStatusEnum?.INVESTIGATION_ONGOING?.toLocaleLowerCase() ||
                prohibitionDataState.status?.toLowerCase() ===
                  AssetReferralActionStatusEnum?.PROHIBITION_ON_REVISION?.toLocaleLowerCase()) &&
                prohibitionDataState.forward_status !==
                  AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR && (
                  <div className="mt-4 text-right">
                    <button
                      onClick={handleForwardToCoordinator}
                      disabled={
                        status ===
                          AssetReferralActionStatusEnum?.PROHIBITION_REQUESTED ||
                        isForwarding
                      }
                      className={`text-sm px-4 py-2 rounded-md font-medium ${
                        status ===
                        AssetReferralActionStatusEnum?.PROHIBITION_REQUESTED
                          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                          : "bg-black text-white hover:bg-gray-700"
                      }`}
                    >
                      {prohibitionDataState.status ===
                        AssetReferralActionStatusEnum?.PROHIBITION_REQUESTED ||
                      status ===
                        AssetReferralActionStatusEnum?.PROHIBITION_ON_UPDATED
                        ? AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR
                        : isForwarding
                          ? t("forwarding", { defaultValue: "Forwarding..." })
                          : t("forward_to_coordinator")}
                    </button>
                  </div>
                )}
            </>
          )}
          {isAssetCoordinator &&
            (prohibitionDataState.status?.toLowerCase() ===
              AssetReferralActionStatusEnum?.PROHIBITION_REQUESTED?.toLocaleLowerCase() ||
              prohibitionDataState.status?.toLowerCase() ===
                AssetReferralActionStatusEnum?.PROHIBITION_ON_UPDATED?.toLocaleLowerCase()) && (
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={handleProsecutorApproveDecision}
                  disabled={isForwarding}
                  className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-60"
                >
                  {isForwarding &&
                  prohibitionDataState.status === "Prohibition Approved"
                    ? t("approving", { defaultValue: "Approving..." })
                    : t("approve")}
                </button>

                <button
                  onClick={() => setIsRespondBackOpen(true)}
                  disabled={isForwarding}
                  className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-60"
                >
                  {isForwarding && status === "Responded Back"
                    ? t("responding", { defaultValue: "Responding..." })
                    : t("respond_back")}
                </button>
              </div>
            )}

          {/* Prosecutor Assignment Section */}
          <ProsecutorAssignmentSection
            canAssignProsecutor={canAssignProsecutor}
            selectedProsecutor={selectedProsecutor}
            prosecutors={prosecutors}
            referral={referral}
            onProsecutorChange={setSelectedProsecutor}
            onAssignReAssign={handleAssignReAssign}
          />
          {isAssetProsecutor &&
            prohibitionDataState.status === "Prohibition On litigation" &&
            prohibitionDataState.forward_status ===
              AssetReferralForwardingStatusEnum.ASSIGNED_TO_PROSECUTOR && (
              <div className="mt-4 flex justify-end border-t pt-4 mx-auto">
                <button
                  onClick={() => {
                    setActiveTab?.("Prohibition Reports");

                    onClose(); // Close current popup
                  }}
                  className="mx-auto w-[30%] text-base justify-center px-4 py-2 font-medium bg-black text-white rounded-md hover:bg-gray-700 flex items-center gap-2 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {t("add_report")}
                </button>
              </div>
            )}
          {/* Main Content */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <InfoRow
              label={t("status")}
              value={formatNullableValue(prohibitionDataState.status)}
            />
            <InfoRow
              label={t("forward_status", { defaultValue: "Forward Status" })}
              value={formatNullableValue(prohibitionDataState.forward_status)}
            />
            <InfoRow
              label={t("summary")}
              value={formatNullableValue(
                prohibitionDataState.prohabition_request_summary,
              )}
            />
            {prohibitionDataState.prohibition_request_documents &&
              prohibitionDataState.prohibition_request_documents?.length >
                0 && (
                <div className="flex flex-col">
                  <span style={{ color: "#432101AB" }}>
                    {t("appeal_documents")}
                  </span>
                  <div style={{ color: "var(--Prime-Blue, #0A1D39)" }}>
                    {prohibitionDataState.prohibition_request_documents?.map(
                      (file, index) => (
                        <FileRow file={file} key={index} {...fileRowProps} />
                      ),
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Request Information */}
          {hasRegistrationData && (
            <>
              <SectionTitle
                title={t("request_information", {
                  defaultValue: "Request Information",
                })}
              />
              <div className="bg-gray-50 p-3 rounded-lg">
                <InfoRow
                  label={t("registration_number")}
                  value={formatNullableValue(
                    prohibitionDataState.prohabition_request_registration_number,
                  )}
                />
                <InfoRow
                  label={t("registration_date")}
                  value={formatDate(
                    prohibitionDataState.prohabiton_request_date_registrar,
                  )}
                />
              </div>
            </>
          )}

          {/* Court Information */}
          {hasCourtData && (
            <>
              {" "}
              <SectionTitle
                title={t("court_information", {
                  defaultValue: "Court Information",
                })}
              />
              <div className="bg-gray-50 p-3 rounded-lg">
                <InfoRow
                  label={t("court_name")}
                  value={formatNullableValue(
                    prohibitionDataState.prohabition_court_name,
                  )}
                />
                <InfoRow
                  label={t("court_level")}
                  value={formatNullableValue(
                    prohibitionDataState.prohabition_court_level,
                  )}
                />
                <InfoRow
                  label={t("region")}
                  value={formatNullableValue(
                    prohibitionDataState.prohabition_court_region,
                  )}
                />
                <InfoRow
                  label={t("zone")}
                  value={formatNullableValue(
                    prohibitionDataState.prohabition_court_zone,
                  )}
                />
                <InfoRow
                  label={t("woreda")}
                  value={formatNullableValue(
                    prohibitionDataState.prohabition_court_woreda,
                  )}
                />
                <InfoRow
                  label={t("court_registration")}
                  value={formatNullableValue(
                    prohibitionDataState.prohabition_request_court_registration_number,
                  )}
                />
                <InfoRow
                  label={t("first_hearing_date")}
                  value={formatDate(
                    prohibitionDataState.prohabition_request_first_hearing_date,
                  )}
                />
              </div>
            </>
          )}

          {/* Assets Information */}
          <SectionTitle
            title={t("assets_information", {
              defaultValue: "Assets Information",
            })}
          />
          <div className="bg-gray-50 p-3 rounded-lg">
            <InfoRow
              label={t("suspect_assets", { defaultValue: "Suspect Assets" })}
              value={formatArrayValue(prohibitionDataState.count_suspect_asset)}
            />
            {prohibitionDataState.count_suspect_asset.length > 0 && (
              <div className="mt-2">
                <span className="text-xs text-gray-500">
                  {t("total_assets", { defaultValue: "Total assets" })}:{" "}
                  {prohibitionDataState.count_suspect_asset.length}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Prohibition Section */}
      {!hasCourtData && (
        <ProhibitionSection
          isEditMode={isEditMode}
          isAssetRegistrar={isAssetRegistrar}
          referral={referral}
          prohibitionFormData={prohibitionFormData}
          isEditingProhibition={isEditingProhibition}
          showProhibitionForm={showProhibitionForm}
          hasProhibitionData={hasRegistrationData}
          hasCourtData={hasCourtData}
          onProhibitionChange={handleProhibitionChange}
          onProhibitionSubmit={handleProhibitionSubmit}
          onProhibitionEditClick={handleProhibitionEditClick}
          onCancelProhibitionEdit={handleCancelProhibitionEdit}
          onAddProhibitionDetails={handleAddProhibitionDetails}
          onForwardToCoordinator={handleProhibitionForwardToCoordinator}
          onOpenCourtPopup={() => setIsProhibitionCourtPopupOpen(true)}
          prohibitionData={prohibitionDataState}
          setProhibitionDataState={setProhibitionDataState}
        />
      )}

      {/* Court Section */}
      {hasRegistrationData && (
        <CourtSection
          hasCourtData={hasCourtData}
          referral={referral}
          onForwardToCoordinator={handleProhibitionForwardToCoordinator}
          onOpenCourtPopup={() => setIsProhibitionCourtPopupOpen(true)}
          isAssetRegistrar={isAssetRegistrar}
          isEditMode={isEditMode}
          isEditing={isEditing}
          prohibitionData={prohibitionDataState}
          onEditClick={() => setIsProhibitionCourtPopupOpen(true)}
        />
      )}

      {/* Respond Back Popup */}
      <RespondBackPopup
        isOpen={isRespondBackOpen}
        onClose={() => setIsRespondBackOpen(false)}
        referralId={prohibitionDataState.referral as string}
        onNewDataAdded={async (data) => {
          if (onDecsionCreated) {
            onDecsionCreated(data);
          }
          await refreshProhibitionData();
        }}
        setActiveTab={setActiveTab}
      />

      {/* Court Popup */}
      <ProhibitionCourtPopup
        isOpen={isProhibitionCourtPopupOpen}
        onClose={() => setIsProhibitionCourtPopupOpen(false)}
        referralId={referralId as string}
        prohibitionId={prohibitionDataState.id as string}
        onSuccess={handleCourtPopupSuccess}
      />
    </BasePopup>
  );
};
